import { DateTime } from "luxon";
import { useParameters } from "@/store/useParameters";

/**
 * Get the AOW age in months for a given birth date.
 * Uses the aowLeeftijd parameter which contains the official Dutch AOW age thresholds.
 */
function getAOWAgeInMonths(birthDate: DateTime): number {
	const birthDateString = birthDate.toISODate();
	if (!birthDateString) {
		return 70 * 12; // Default fallback
	}

	// Get the AOW age thresholds from parameters
	// The parameter date doesn't matter for AOW ages, they're based on birth date
	const aowLeeftijden = useParameters("2024-01-01").aowLeeftijd;

	// Find the applicable AOW age by finding the last threshold where
	// the birth date is on or after the 'vanaf' date
	const applicableThreshold = aowLeeftijden.findLast(
		(threshold: { vanaf: string; jaar: number; maanden?: number }) =>
			birthDateString >= threshold.vanaf,
	);

	if (applicableThreshold) {
		return applicableThreshold.jaar * 12 + (applicableThreshold.maanden ?? 0);
	}

	return 65 * 12;
}

/**
 * Calculate the AOW start date for a person based on their birth date.
 * AOW always starts on the first day of the birth month, not on the actual birthday.
 * E.g., someone born on Jan 10 becomes AOW-eligible from Jan 1.
 */
function getAOWStartDate(birthDate: DateTime): DateTime {
	const aowAgeMonths = getAOWAgeInMonths(birthDate);
	return birthDate.plus({ months: aowAgeMonths }).set({ day: 1 });
}

/**
 * Check if a person is eligible for AOW (has reached AOW age) on a given date.
 *
 * @param birthDate - The person's birth date (string in ISO format or DateTime)
 * @param calculationDate - The date to check eligibility for (string in ISO format or DateTime)
 * @returns boolean - true if the person has reached AOW age on the calculation date
 */
export function isEligibleForAOW(
	birthDate: string | DateTime | undefined,
	calculationDate: string | DateTime | undefined,
): boolean {
	if (!birthDate || !calculationDate) {
		return false;
	}

	const birthDateTime =
		typeof birthDate === "string" ? DateTime.fromISO(birthDate) : birthDate;
	const calculationDateTime =
		typeof calculationDate === "string"
			? DateTime.fromISO(calculationDate)
			: calculationDate;

	if (!birthDateTime.isValid || !calculationDateTime.isValid) {
		return false;
	}

	const aowStartDate = getAOWStartDate(birthDateTime);
	return calculationDateTime >= aowStartDate;
}

/**
 * Composable to check AOW eligibility for customers.
 * This is a convenience wrapper for use in Vue components.
 */
export function useAOWEligibility() {
	return {
		isEligibleForAOW,
		getAOWStartDate: (birthDate: string | DateTime | undefined) => {
			if (!birthDate) return undefined;
			const birthDateTime =
				typeof birthDate === "string" ? DateTime.fromISO(birthDate) : birthDate;
			if (!birthDateTime.isValid) return undefined;
			return getAOWStartDate(birthDateTime);
		},
	};
}
